<template>
  <div class="payout-event-detail">
    <div v-if="assignedRoles.length === 0" class="text-center text-muted py-3">
      <translate translate-context="collaborators.detail">No roles assigned</translate>
    </div>
    <div class="collab-table">
      <div
        v-for="(role, index) in assignedRoles"
        :key="`${role.id}-${index}`"
        class="p-3"
        :class="{'border-bottom': index !== assignedRoles.length - 1}"
      >
        <div class="d-flex justify-content-between align-items-center">
          <!-- Left: role name -->
          <div class="font-weight-medium">
            <div class="mb-1">{{ role.display_name || role.name }}</div>
            <div v-if="role.assigned_at" class="text-muted small mr-3">
              <CIcon name="cipCalendar" size="custom-size" :height="16" class="mr-1"
                     style="vertical-align: text-bottom;"/>
              {{ formatRoleDate(role.assigned_at) }}
            </div>
          </div>

          <div class="d-flex align-items-center">
            <!-- Status badge -->
            <CBadge :color="roleStatusColor(role)" class="mr-2 badge-outline">
              {{ roleStatusLabel(role) }}
            </CBadge>

            <!-- Gear menu (only for direct editable roles) -->
            <CDropdown
              v-if="!role.is_inherited && role.is_editable"
              color="link"
              size="sm"
              :caret="false"
              placement="bottom-end"
              class="role-menu"
            >
              <template #toggler-content>
                <CIcon name="cipDotsVertical" size="sm" style="color: #4E4D64"/>
              </template>

              <!-- View permissions -->
              <CDropdownItem @click="$emit('view-role-permissions', role)">
                <CIcon name="cipEye" size="custom-size" :height="16" class="mr-2"/>
                <translate translate-context="collaborators.detail">View permissions</translate>
              </CDropdownItem>

              <CDropdownDivider/>

              <!-- Disable (if active) -->
              <CDropdownItem
                v-if="isRoleActive(role)"
                @click="confirmDisableRole(role)"
              >
                <CIcon name="cipSlashCircle01" size="custom-size" :height="16" class="mr-2"/>
                <translate translate-context="collaborators.detail">Disable role</translate>
              </CDropdownItem>

              <!-- Enable (if disabled) -->
              <CDropdownItem
                v-else
                @click="confirmEnableRole(role)"
              >
                <CIcon name="cipCircleCheck" size="custom-size" :height="16" class="mr-2"/>
                <translate translate-context="collaborators.detail">Enable role</translate>
              </CDropdownItem>

              <CDropdownDivider/>

              <!-- Delete -->
              <CDropdownItem @click="confirmDeleteRole(role)">
                <CIcon name="cipTrashFull" size="custom-size" :height="16" class="mr-2 text-danger"/>
                <span class="text-danger">
                <translate translate-context="collaborators.detail">Delete role</translate>
              </span>
              </CDropdownItem>
            </CDropdown>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'CollaboratorRoleDetail',

  props: {
    collaborator: {
      type: Object,
      required: true
    },
    availableRoles: {
      type: Array,
      default: () => []
    },
    scope: {
      type: Object,
      default: () => ({})
    }
  },

  computed: {
    assignedRoles() {
      const all = this.collaborator.roles || []
      // Direct roles first, inherited after
      return [...all].sort((a, b) => (a.is_inherited ? 1 : 0) - (b.is_inherited ? 1 : 0))
    },

    hasAssignableRoles() {
      const currentRoleIds = new Set((this.collaborator.roles || []).map(r => r.id))
      return this.availableRoles.some(r => !currentRoleIds.has(r.id))
    }
  },

  methods: {
    isRoleActive(role) {
      return role.is_active !== false
    },

    roleStatusColor(role) {
      if (role.is_inherited) return 'info'
      if (!this.isRoleActive(role)) return 'secondary'
      return 'success'
    },

    roleStatusLabel(role) {
      if (role.is_inherited) return this.$pgettext('collaborators.detail', 'Inherited')
      if (!this.isRoleActive(role)) return this.$pgettext('collaborators.detail', 'Disabled')
      return this.$pgettext('collaborators.detail', 'Active')
    },

    confirmDisableRole(role) {
      this.confirm({
        title: this.$pgettext('collaborators.confirm', 'Disable Role'),
        message: this.$pgettext('collaborators.confirm', 'Are you sure you want to disable this role?'),
        cb_confirm: () => {
          this.$emit('disable-role', {
            collaboratorId: this.collaborator.id,
            roleId: role.id
          })
        },
        cb_decline: () => {
        }
      })
    },

    confirmEnableRole(role) {
      this.confirm({
        title: this.$pgettext('collaborators.confirm', 'Enable Role'),
        message: this.$pgettext('collaborators.confirm', 'Are you sure you want to enable this role?'),
        cb_confirm: () => {
          this.$emit('enable-role', {
            collaboratorId: this.collaborator.id,
            roleId: role.id
          })
        },
        cb_decline: () => {
        }
      })
    },

    confirmDeleteRole(role) {
      this.confirm({
        title: this.$pgettext('collaborators.confirm', 'Delete Role'),
        message: this.$pgettext('collaborators.confirm', 'Are you sure you want to permanently remove this role from the user?'),
        color: 'danger',
        cb_confirm: () => {
          this.$emit('delete-role', {
            collaboratorId: this.collaborator.id,
            roleId: role.id
          })
        },
        cb_decline: () => {
        }
      })
    },

    formatRoleDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})
    }
  }
}
</script>

<style scoped>
.role-detail-section {
  padding: 0.75rem 1.5rem 0.5rem;
  background: #f8f9fa;
  border-radius: 8px;
  margin: 0.5rem 1rem 1rem 1rem;
}

.role-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 0.75rem 1rem;
}

.role-card-inherited {
  background: #f0f4f8;
  border-color: #d0d9e3;
}

.role-card-disabled {
  background: #f9fafb;
  border-color: #e5e7eb;
}

.role-card-disabled .role-name {
  color: #9ca3af;
}

.role-menu {
  margin-left: 2px;
}
</style>
